import {
  faExchangeAlt,
  faHeadset,
  faHome,
  faSignOutAlt,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import {
  Navbar as BSNavbar,
  Container,
  Dropdown,
  Image,
  Nav,
} from 'react-bootstrap';
import { IoMdArrowDropdown } from 'react-icons/io';
import { useQueryClient } from 'react-query';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useFetchAccount, useFetchStore, useSignOut } from '../../api/account';
import codecanyonLogo from '../../assets/images/codecanyon.png';
import magentoLogo from '../../assets/images/magento.svg';
import printcartLogo from '../../assets/images/mini-logo.png';
import shopifyLogo from '../../assets/images/shopify-icon.svg';
import wixLogo from '../../assets/images/wix-icon.svg';
import woocommerceLogo from '../../assets/images/woocommerce-plain.svg';
import { Routes } from '../../routes';
import styles from './Navbar.module.css';
import ProductSyncNotification from './ProductSyncNotification';

const VerticalLine = (props) => {
  const { showService = true, showStoreUrl = true } = props;
  return (
    <>
      {showService && showStoreUrl && (
        <div style={{ width: '1px', height: '24px' }} className="bg-gray-400" />
      )}
    </>
  );
};

const Navbar = (props) => {
  const {
    height = 54,
    showService = true,
    showSyncProduct = true,
    showStoreUrl = true,
  } = props;

  const history = useHistory();

  const { data, isLoading: isAccountLoading } = useFetchAccount();
  const { data: storeData, isLoading: isStoreLoading } = useFetchStore();

  const getLogo = (type) => {
    let logo = printcartLogo;
    let borderColor = '#5b6df2';

    switch (type) {
      case 'woocommerce':
        logo = woocommerceLogo;
        borderColor = '#764ca1';
        break;
      case 'magentoLogo':
        logo = magentoLogo;
        borderColor = '#ce6d44';
        break;
      case 'codecanyon':
        logo = codecanyonLogo;
        borderColor = '#050505';
        break;
      case 'shopify':
        logo = shopifyLogo;
        borderColor = '#6d9045';
        break;
      case 'wix':
        logo = wixLogo;
        borderColor = '#050505';
        break;
      default:
        logo = printcartLogo;
        borderColor = '#5b6df2';
        break;
    }

    return { logo, borderColor };
  };

  const queryClient = useQueryClient();
  const { mutate, isLoading } = useSignOut();

  const signOutAction = async () => {
    mutate(undefined, {
      onSuccess: () => {
        history.push(Routes.Signin.path);
        localStorage.removeItem('_pc-s');
        localStorage.removeItem('_pc-t');
        localStorage.removeItem('_pc-ws');
        queryClient.resetQueries(['account']);
      },
      onError: (err) => {
        toast.error(err.message);
      },
    });
  };

  return (
    <BSNavbar
      expanded
      className="ps-3 pe-3 py-0 main-nav border-bottom border-gray-200 bg-gray-100"
      style={{
        height,
      }}
      sticky="top"
    >
      <Container fluid>
        <div className="d-flex justify-content-end align-items-center w-100">
          {data && storeData && (
            <Nav className="align-items-center">
              {showService && (
                <Nav.Item>
                  <Dropdown>
                    <Dropdown.Toggle
                      className={styles.dropdownToggle}
                      variant="link"
                    >
                      <FontAwesomeIcon icon={faHeadset} className="me-2 fs-5" />
                      <div className="d-none d-md-block">Our Support</div>
                      <IoMdArrowDropdown />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item as={Link} to={Routes.HelpDesk.path}>
                        Helpdesk
                      </Dropdown.Item>
                      <Dropdown.Item as={Link} to={Routes.ProjectTicket.path}>
                        Ticket
                      </Dropdown.Item>
                      <Dropdown.Item as={Link} to={Routes.Project.path}>
                        Project
                      </Dropdown.Item>
                      <Dropdown.Item as={Link} to={Routes.Integration.path}>
                        Integration
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </Nav.Item>
              )}
              <VerticalLine showService={showService} />
              {/* Product Sync Notification */}
              {showSyncProduct && (
                <Nav.Item>
                  <ProductSyncNotification showService={showService} />
                </Nav.Item>
              )}
              <VerticalLine showService={showService} showStoreUrl={showStoreUrl} />
              {showStoreUrl && (
                <Nav.Item>
                  <Dropdown>
                    <Dropdown.Toggle
                      className={styles.dropdownToggle}
                      variant="link"
                    >
                      <FontAwesomeIcon icon={faHome} className="me-2 fs-5" />
                      <div className="d-none d-md-block">Your Store</div>
                      <IoMdArrowDropdown />
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item
                        href={storeData.shop_url ? storeData.shop_url : null}
                        target="_blank"
                        className="d-flex align-items-center"
                      >
                        Storefont
                      </Dropdown.Item>
                      <Dropdown.Item as={Link} to={Routes.Settings.path}>
                        Store Detail
                      </Dropdown.Item>
                      <Dropdown.Item as={Link} to={Routes.SettingsSecurity.path}>
                        Store Security
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </Nav.Item>
              )}
              <VerticalLine showService={showService} showStoreUrl={showStoreUrl} />
              <Nav.Item>
                <Dropdown as={Nav.Item} align="end">
                  <Dropdown.Toggle className={styles.dropdownToggle} as={Nav.Link}>
                    <div className="media d-flex align-items-center">
                      <div
                        className="me-2 p-1 rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          borderColor: getLogo(storeData?.integration_type)
                            .borderColor,
                        }}
                      >
                        <Image
                          style={{
                            width: '20px',
                            height: '20px',
                          }}
                          src={getLogo(storeData?.integration_type).logo}
                        />
                      </div>

                      <div className="media-body align-items-center d-none d-xl-flex">
                        <span>{storeData.name}</span>
                        <IoMdArrowDropdown />
                      </div>
                    </div>
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {data?.email && (
                      <Dropdown.ItemText className="text-muted w-100">
                        {data.email}
                      </Dropdown.ItemText>
                    )}
                    <Dropdown.Divider className="border-gray-400" />
                    <Dropdown.Item
                      as={Nav.Link}
                      href={Routes.StoreList.path}
                      className="text-dark"
                    >
                      <FontAwesomeIcon
                        icon={faExchangeAlt}
                        className="me-2 text-gray-500"
                      />
                      Switch Store
                    </Dropdown.Item>

                    <Dropdown.Divider className="border-gray-400" />

                    <Dropdown.Item
                      as={Nav.Link}
                      disabled={isLoading || isAccountLoading || isStoreLoading}
                      className="text-dark"
                      onClick={(e) => {
                        e.preventDefault();
                        signOutAction();
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faSignOutAlt}
                        className="me-2 text-gray-500"
                      />
                      Logout
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Nav.Item>
            </Nav>
          )}
        </div>
      </Container>
    </BSNavbar>
  );
};

export default Navbar;
